import 'ol/ol.css';
import 'ol-ext/dist/ol-ext.css';

// Core imports
import Collection from 'ol/Collection';
import Disposable from 'ol/Disposable';
import Feature from 'ol/Feature';
import Geolocation from 'ol/Geolocation';
import Kinetic from 'ol/Kinetic';
import Map from 'ol/Map';
import MapBrowserEvent from 'ol/MapBrowserEvent';
import MapEvent from 'ol/MapEvent';
import OLObject from 'ol/Object';
import Observable from 'ol/Observable';
import Overlay from 'ol/Overlay';
import View from 'ol/View';

// Layer imports
import BaseLayer from 'ol/layer/Base';
import Group from 'ol/layer/Group';
import Heatmap from 'ol/layer/Heatmap';
import ImageLayer from 'ol/layer/Image';
import Layer from 'ol/layer/Layer';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorImageLayer from 'ol/layer/VectorImage';
import VectorTileLayer from 'ol/layer/VectorTile';
import WebGLTileLayer from 'ol/layer/WebGLTile';
import * as layerMod from 'ol/layer';

// Source imports
import BingMaps from 'ol/source/BingMaps';
import CartoDB from 'ol/source/CartoDB';
import Cluster from 'ol/source/Cluster';
import DataTile from 'ol/source/DataTile';
import ImageArcGISRest from 'ol/source/ImageArcGISRest';
import ImageCanvas from 'ol/source/ImageCanvas';
import ImageStatic from 'ol/source/ImageStatic';
import ImageWMS from 'ol/source/ImageWMS';
import OSM from 'ol/source/OSM';
import Raster from 'ol/source/Raster';
import Source from 'ol/source/Source';
import StadiaMaps from 'ol/source/StadiaMaps';
import TileArcGISRest from 'ol/source/TileArcGISRest';
import TileDebug from 'ol/source/TileDebug';
import TileImage from 'ol/source/TileImage';
import TileJSON from 'ol/source/TileJSON';
import TileWMS from 'ol/source/TileWMS';
import UrlTile from 'ol/source/UrlTile';
import UTFGrid from 'ol/source/UTFGrid';
import VectorSource from 'ol/source/Vector';
import VectorTileSource from 'ol/source/VectorTile';
import WMTS from 'ol/source/WMTS';
import XYZ from 'ol/source/XYZ';
import Zoomify from 'ol/source/Zoomify';
import * as sourceMod from 'ol/source';

// Geometry imports
import Circle from 'ol/geom/Circle';
import GeometryCollection from 'ol/geom/GeometryCollection';
import Geometry from 'ol/geom/Geometry';
import LinearRing from 'ol/geom/LinearRing';
import LineString from 'ol/geom/LineString';
import MultiLineString from 'ol/geom/MultiLineString';
import MultiPoint from 'ol/geom/MultiPoint';
import MultiPolygon from 'ol/geom/MultiPolygon';
import Point from 'ol/geom/Point';
import Polygon from 'ol/geom/Polygon';
import SimpleGeometry from 'ol/geom/SimpleGeometry';
import * as geomMod from 'ol/geom';

// Style imports
import CircleStyle from 'ol/style/Circle';
import Fill from 'ol/style/Fill';
import Icon from 'ol/style/Icon';
import IconImage from 'ol/style/IconImage';
import Image from 'ol/style/Image';
import RegularShape from 'ol/style/RegularShape';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import Text from 'ol/style/Text';
import * as styleMod from 'ol/style';

// Projection imports
import Projection from 'ol/proj/Projection';
import * as projMod from 'ol/proj';
import { fromLonLat, toLonLat, transform, get as getProjection, addProjection, addCoordinateTransforms, equivalent, getTransform } from 'ol/proj';

// Format imports
import EsriJSON from 'ol/format/EsriJSON';
import FormatFeature from 'ol/format/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import GML from 'ol/format/GML';
import GML2 from 'ol/format/GML2';
import GML3 from 'ol/format/GML3';
import GPX from 'ol/format/GPX';
import IGC from 'ol/format/IGC';
import JSONFeature from 'ol/format/JSONFeature';
import KML from 'ol/format/KML';
import MVT from 'ol/format/MVT';
import OSMXML from 'ol/format/OSMXML';
import Polyline from 'ol/format/Polyline';
import TopoJSON from 'ol/format/TopoJSON';
import WFS from 'ol/format/WFS';
import WKT from 'ol/format/WKT';
import WMSCapabilities from 'ol/format/WMSCapabilities';
import WMSGetFeatureInfo from 'ol/format/WMSGetFeatureInfo';
import WMTSCapabilities from 'ol/format/WMTSCapabilities';
import XMLFeature from 'ol/format/XMLFeature';
import * as formatMod from 'ol/format';

// Control imports
import Attribution from 'ol/control/Attribution';
import Control from 'ol/control/Control';
import FullScreen from 'ol/control/FullScreen';
import MousePosition from 'ol/control/MousePosition';
import OverviewMap from 'ol/control/OverviewMap';
import Rotate from 'ol/control/Rotate';
import ScaleLine from 'ol/control/ScaleLine';
import Zoom from 'ol/control/Zoom';
import ZoomSlider from 'ol/control/ZoomSlider';
import ZoomToExtent from 'ol/control/ZoomToExtent';
import { defaults as controlDefaults } from 'ol/control';
import * as controlMod from 'ol/control';

// Interaction imports
import DoubleClickZoom from 'ol/interaction/DoubleClickZoom';
import DragAndDrop from 'ol/interaction/DragAndDrop';
import DragBox from 'ol/interaction/DragBox';
import DragPan from 'ol/interaction/DragPan';
import DragRotate from 'ol/interaction/DragRotate';
import DragRotateAndZoom from 'ol/interaction/DragRotateAndZoom';
import DragZoom from 'ol/interaction/DragZoom';
import Draw, {createBox} from 'ol/interaction/Draw';
import Extent from 'ol/interaction/Extent';
import Interaction from 'ol/interaction/Interaction';
import KeyboardPan from 'ol/interaction/KeyboardPan';
import KeyboardZoom from 'ol/interaction/KeyboardZoom';
import Modify from 'ol/interaction/Modify';
import MouseWheelZoom from 'ol/interaction/MouseWheelZoom';
import PinchRotate from 'ol/interaction/PinchRotate';
import PinchZoom from 'ol/interaction/PinchZoom';
import Pointer from 'ol/interaction/Pointer';
import Select from 'ol/interaction/Select';
import Snap from 'ol/interaction/Snap';
import Translate from 'ol/interaction/Translate';
import { defaults as interactionDefaults } from 'ol/interaction';
import * as interactionMod from 'ol/interaction';

// Events imports
import * as events from 'ol/events';
import Event from 'ol/events/Event';
import * as condition from 'ol/events/condition';
import {
    click,
    singleClick,
    doubleClick,
    never,
    always,
    focus,
    pointerMove,
    altKeyOnly,
    altShiftKeysOnly,
    shiftKeyOnly,
    targetNotEditable,
    mouseOnly,
    primaryAction,
    noModifierKeys,
    platformModifierKeyOnly
} from 'ol/events/condition';

// Add this import for render Feature
import RenderFeature from 'ol/render/Feature';

// Coordinate and extent utilities
import * as coordinate from 'ol/coordinate';
import * as extent from 'ol/extent';
import * as sphere from 'ol/sphere';

// Utilities
import * as color from 'ol/color';
import * as easing from 'ol/easing';
import { getUid, VERSION } from 'ol/util';

// ol-ext controls
import LayerSwitcher from 'ol-ext/control/LayerSwitcher';
import Popup from 'ol-ext/overlay/Popup';
import SearchNominatim from 'ol-ext/control/SearchNominatim';

// ol-ext interactions
import Transform from 'ol-ext/interaction/Transform';

// Expose comprehensive OpenLayers API globally
Draw.createBox = createBox;
window.ol = {
    // Core classes
    Collection,
    Disposable,
    Feature,
    Geolocation,
    Kinetic,
    Map,
    MapBrowserEvent,
    MapEvent,
    Object: OLObject,
    Observable,
    Overlay,
    View,
    
    // Layer namespace
    layer: {
        Base: BaseLayer,
        Group,
        Heatmap,
        Image: ImageLayer,
        Layer,
        Tile: TileLayer,
        Vector: VectorLayer,
        VectorImage: VectorImageLayer,
        VectorTile: VectorTileLayer,
        WebGLTile: WebGLTileLayer
    },
    
    // Source namespace
    source: {
        BingMaps,
        CartoDB,
        Cluster,
        DataTile,
        ImageArcGISRest,
        ImageCanvas,
        ImageStatic,
        ImageWMS,
        OSM,
        Raster,
        Source,
        StadiaMaps,
        TileArcGISRest,
        TileDebug,
        // TileImage,
        TileJSON,
        TileWMS,
        // UrlTile,
        UTFGrid,
        Vector: VectorSource,
        VectorTile: VectorTileSource,
        WMTS,
        XYZ,
        Zoomify
    },
    
    // Geometry namespace
    geom: {
        Circle,
        Geometry,
        GeometryCollection,
        LinearRing,
        LineString,
        MultiLineString,
        MultiPoint,
        MultiPolygon,
        Point,
        Polygon,
        SimpleGeometry
    },
    
    // Style namespace
    style: {
        Circle: CircleStyle,
        Fill,
        Icon,
        IconImage,
        Image,
        RegularShape,
        Stroke,
        Style,
        Text
    },
    
    // Projection namespace
    proj: {
        Projection,
        fromLonLat,
        toLonLat,
        transform,
        get: getProjection,
        addProjection,
        addCoordinateTransforms,
        equivalent,
        getTransform,
        transformExtent: projMod.transformExtent,
        useGeographic: projMod.useGeographic,
        getPointResolution: projMod.getPointResolution,
        addEquivalentProjections: projMod.addEquivalentProjections,
        addEquivalentTransforms: projMod.addEquivalentTransforms,
        clearAllProjections: projMod.clearAllProjections,
        createProjection: projMod.createProjection,
        createSafeCoordinateTransform: projMod.createSafeCoordinateTransform,
        createTransformFromCoordinateTransform: projMod.createTransformFromCoordinateTransform,
        disableCoordinateWarning: projMod.disableCoordinateWarning,
        // epsg3857: projMod.epsg3857,
        // epsg4326: projMod.epsg4326,
        fromUserCoordinate: projMod.fromUserCoordinate,
        fromUserExtent: projMod.fromUserExtent,
        getTransformFromProjections: projMod.getTransformFromProjections,
        getUserProjection: projMod.getUserProjection,
        identityTransform: projMod.identityTransform,
        setUserProjection: projMod.setUserProjection,
        toUserCoordinate: projMod.toUserCoordinate,
        toUserExtent: projMod.toUserExtent
    },
    
    // Format namespace
    format: {
        EsriJSON,
        Feature: FormatFeature,
        GeoJSON,
        GML,
        GML2,
        GML3,
        GPX,
        IGC,
        JSONFeature,
        KML,
        MVT,
        OSMXML,
        Polyline,
        TopoJSON,
        WFS,
        WKT,
        WMSCapabilities,
        WMSGetFeatureInfo,
        WMTSCapabilities,
        XMLFeature
    },
    
    // Control namespace
    control: {
        Attribution,
        Control,
        FullScreen,
        MousePosition,
        OverviewMap,
        Rotate,
        ScaleLine,
        Zoom,
        ZoomSlider,
        ZoomToExtent,
        LayerSwitcher,
        // expose the defaults function directly (ol.control.defaults is a function)
        defaults: controlDefaults
    },
    
    // Interaction namespace
    interaction: {
        DoubleClickZoom,
        DragAndDrop,
        DragBox,
        DragPan,
        DragRotate,
        DragRotateAndZoom,
        DragZoom,
        Draw,
        Extent,
        Interaction,
        KeyboardPan,
        KeyboardZoom,
        Modify,
        MouseWheelZoom,
        PinchRotate,
        PinchZoom,
        Pointer,
        Select,
        Snap,
        Translate,
        Transform,
        defaults: { defaults: interactionDefaults}
    },
    
    // Overlay namespace
    overlay: {
        Popup
    },
    
    // Events namespace
    // expose the imported events module and the condition helpers so code that
    // references `ol.events` or `ol.events.condition.click` will work.
    events: Object.assign({}, events, { condition: Object.assign({}, condition, {
        click,
        singleClick,
        doubleClick,
        never,
        always,
        focus,
        pointerMove,
        altKeyOnly,
        altShiftKeysOnly,
        shiftKeyOnly,
        targetNotEditable,
        mouseOnly,
        primaryAction,
        noModifierKeys,
        platformModifierKeyOnly
    }) }),
    
    // Utilities
    coordinate,
    extent,
    sphere,
    color,
    easing,
    
    // Utility functions
    getUid,
    VERSION,
    
    // Direct references for convenience
    transform,
    toLonLat,
    fromLonLat,
    getProjection,
    
    // Add render namespace with Feature prototype
    render: {
        Feature: {
            prototype: RenderFeature.prototype
        }
    }
};

// Also expose some commonly-used top-level aliases to match older code that
// expects them directly on `ol` (instead of `ol.proj` etc.)
window.ol.Projection = Projection;
window.ol.get = getProjection; // ol.get(...) sometimes used as shorthand

// ol-ext namespace
window.olx = {
    LayerSwitcher,
    Popup,
    SearchNominatim
};

// Additional shortcuts
window.MapOL = Map;
window.ol.layer.Graticule = window.ol.layer.Group; // Alias if needed